///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { GtthProgressWindowService } from './progress-window.service';
import { GtthProgressWindowModel } from './progress-window.model';
import { GtthAbortDesignCommand } from '../../logical-topology/engine/commands/design/abort-design-command';
import { GtthActivateDesignIterationCommand } from '../../logical-topology/engine/commands/design/activate-design-iteration-command';
import { GtthHistoryService } from '../../logical-topology/engine/history-service';
import { Router } from '@angular/router';
import { UserAuthorities } from '../../../services/user/user-authorities';
import { GtthAbortOperationCommand } from '../../logical-topology/engine/commands/design/abort-operation-command';
import { GtthRequestLockProjectCommand } from '../../logical-topology/engine/commands/project/request-lock-project-command';
import { ProjectProductsService } from '../../logical-topology/engine/project-products-service';
import { Principal } from '../../../shared/auth/principal.service';
import { GtthProjectStateService } from '../../logical-topology/engine/project-state.service';
import { GtthProjectUserSessionService } from '../../logical-topology/engine/project-user-session.service';
import { isDefined } from '@siklu/sui-lib/utils';

@Component({
    selector: 'winde-progress-window',
    templateUrl: './progress-window.component.html',
    styleUrls: ['./progress-window.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GtthProgressWindowComponent implements OnInit, OnDestroy {
    public isShow = false;
    public windowModel: GtthProgressWindowModel;
    public readonly ITERATIONS_QUANTITY = 20;
    public windowTitle: string;
    public preloaderText: string;
    public progressWindowModel = GtthProgressWindowModel;
    public historyService: GtthHistoryService;
    public hoverIteration;
    public currentDesignValues;
    public currencyCode: string;
    public showCloseProjectBtn: boolean;
    public selectedDesignIteration: number;

    private readonly cancelingTitle = 'Canceling ...';
    private showSubscriber: Subscription;

    constructor(
        private readonly changeDetector: ChangeDetectorRef,
        private readonly progressWindowService: GtthProgressWindowService,
        private readonly projectProductsService: ProjectProductsService,
        private readonly principal: Principal,
        private readonly router: Router,
        private readonly projectStateService: GtthProjectStateService,
        private readonly projectUserSessionService: GtthProjectUserSessionService
    ) {}

    ngOnInit(): void {
        this.historyService = this.progressWindowService.historyService;
        this.currencyCode = this.projectProductsService.getCurrencyCode();

        this.showSubscriber = this.progressWindowService.progressWindow$.subscribe((windowModel: GtthProgressWindowModel) => {
            if (windowModel && windowModel.commandName === GtthProgressWindowModel.ABORT_DESIGN) {
                this.showPreloader(this.cancelingTitle);
                return;
            }

            this.windowModel = windowModel;
            this.isShow = !!this.windowModel;

            if (!this.isShow) {
                this.close();
                return;
            }

            this.setProgressWindowModel();
        });
    }

    ngOnDestroy(): void {
        if (this.showSubscriber) {
            this.showSubscriber.unsubscribe();
            this.showSubscriber = undefined;
        }
    }

    private setProgressWindowModel() {
        switch (this.windowModel.commandName) {
            case GtthProgressWindowModel.AUTO_CONNECT:
                this.windowTitle = `Autoconnect progress`;
                this.showCloseProjectBtn = false;
                break;
            case GtthProgressWindowModel.HOUSE_DETECTION:
                this.windowTitle = `House detection progress`;
                this.showCloseProjectBtn = false;
                break;
            case GtthProgressWindowModel.AUTO_DESIGN:
                this.windowTitle = `Single Design`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.SELECT_PRODUCTS:
                this.windowTitle = `Auto select products`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.DESIGN_PTMP:
                this.windowTitle = `Design PtMP`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.DESIGN_RINGS:
                this.windowTitle = `Design PtP`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.AUTO_SET_FREQUENCIES:
                this.windowTitle = `Auto set frequencies`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.AUTO_CONFIG_RINGS:
                this.windowTitle = `Auto configure rings`;
                this.showCloseProjectBtn = true;
                break;
            case GtthProgressWindowModel.MULTIPLE_DESIGN:
                if (this.windowModel.isCommandFinished()) {
                    this.windowTitle = `Design Results`;
                } else {
                    this.selectedDesignIteration = undefined;
                    this.windowTitle = `Auto-Repeat Design ${(this.windowModel.iterationNumber || 0) + 1} / ${this.ITERATIONS_QUANTITY}`;
                }
                if (this.windowModel.isCommandFinished()) {
                    this.showCurrentIteration();
                }
                this.showCloseProjectBtn = true;
                break;
        }

        if (this.windowModel.isCommandFinished()) {
            this.showPreloader(this.windowModel.preloaderText);
        } else {
            this.changeDetector.markForCheck();
        }
    }

    public close() {
        this.projectUserSessionService.setShowDesignIterations(false);
        if (this.windowModel && !this.windowModel.isCommandFinished()) {
            this.cancelCurrentOperation();
        } else {
            this.isShow = false;
            this.windowModel = undefined;
            this.windowTitle = '';
            this.preloaderText = undefined;
            this.changeDetector.markForCheck();
            this.hoverIteration = undefined;
        }
    }

    public closeProject() {
        this.showPreloader('Closing');
        setTimeout(() => {
            this.principal.hasAnyAuthority(UserAuthorities.ALL_SIKLU).then((hasAuthority) => {
                let url = [];
                if (hasAuthority) {
                    url = ['/project-eng'];
                } else {
                    url = ['/projects'];
                }
                this.router.navigate(url);
            });
        }, 100);
    }

    public cancelCurrentOperation() {
        this.showPreloader(this.cancelingTitle);

        switch (this.windowModel.commandName) {
            case GtthProgressWindowModel.AUTO_CONNECT:
            case GtthProgressWindowModel.HOUSE_DETECTION:
                this.historyService.doCommandBypassHistory(
                    this.historyService.command(GtthAbortOperationCommand).create(this.windowModel.commandName)
                );
                break;
            case GtthProgressWindowModel.AUTO_DESIGN:
            case GtthProgressWindowModel.MULTIPLE_DESIGN:
                this.historyService
                    .doCommandBypassHistory(this.historyService.command(GtthRequestLockProjectCommand).create(false))
                    .then(() => {
                        this.historyService.doCommandBypassHistory(this.historyService.command(GtthAbortDesignCommand));
                    });
                break;
        }
    }

    private showPreloader(text?: string) {
        this.preloaderText = text || undefined;
        this.changeDetector.markForCheck();
    }

    public iterationHover(iterationId) {
        if (isDefined(iterationId)) {
            this.hoverIteration = this.historyService.logicalTopology.designIterationsChartData.find((iteration) => {
                return iteration.id === iterationId;
            });
        } else {
            this.hoverIteration = undefined;
        }

        this.changeDetector.markForCheck();
    }

    selectIteration(iterationId) {
        if (isDefined(iterationId) && this.windowModel.isCommandFinished()) {
            this.historyService.doCommand(this.historyService.command(GtthActivateDesignIterationCommand).create(iterationId));
            this.close();
        }
    }

    showCurrentIteration() {
        if (
            this.historyService.logicalTopology.selectedDesignIteration === undefined ||
            this.historyService.logicalTopology.selectedDesignIteration < 0
        ) {
            return;
        }

        this.selectedDesignIteration = this.historyService.logicalTopology.selectedDesignIteration;

        this.currentDesignValues = this.historyService.logicalTopology.designIterationsChartData.find((iteration) => {
            return iteration.id === this.selectedDesignIteration;
        });

        this.changeDetector.markForCheck();
    }

    @HostListener('window:keyup', ['$event'])
    keyboardInput(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.close();
        }
    }
}
